import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useRegenerate } from 'hooks/queries/designs/useRegenerate'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRootStore } from 'stores/RootStore'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import type { GenerationType } from 'stores/GenerationTimestampStore'

interface UsePerformRegenerationOptions {
  designId: string
  isAiGenerated: boolean
  onSuccess?: () => void
  onError?: (error: string) => void
}

interface PerformRegenerationParams {
  mediaFileKey: string | null
  topic: string
  mediaSourceOverride?: 'ai_video' | 'ai_image' | 'default'
}

export const usePerformRegeneration = ({
  designId,
  isAiGenerated,
  onSuccess,
  onError,
}: UsePerformRegenerationOptions) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { generationTimestampStore } = useRootStore()
  const { mutateAsync: regenerate, isLoading } = useRegenerate({ designId })

  const performRegeneration = useCallback(
    async (params: PerformRegenerationParams) => {
      try {
        await regenerate({
          media_file_key: params.mediaFileKey as string,
          topic: params.topic,
          media_source_override: params.mediaSourceOverride,
        })

        optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, designId, (f) => ({
          ...f,
          is_generating: true,
          image_url_generating: true,
        }))

        updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, designId, (file) => ({
          ...file,
          is_generating: true,
          image_url_generating: true,
        }))

        queryClient.invalidateQueries({ queryKey: filesKeys.detail(workspaceId, designId) })

        const generationType: GenerationType = isAiGenerated ? 'ai' : 'default'
        generationTimestampStore.startGeneration(designId, generationType)

        onSuccess?.()
      } catch {
        const errorMessage = 'Failed to start regeneration. Please try again.'
        onError?.(errorMessage)
        showToastMessage('Failed to start regeneration', 'error')
      }
    },
    [regenerate, queryClient, workspaceId, designId, isAiGenerated, generationTimestampStore, onSuccess, onError],
  )

  return {
    performRegeneration,
    isLoading,
  }
}
